import { Logger } from "../utils/Logger";
import { clearGPTTargeting, setGPTTargeting } from "../googleTagPublisher/gtpUtils";
import { TargetingStorage } from "./TargetingStorage";
import { getUserAdapter } from "../gtm/GtmUtils";
import { UserConsent } from "../cmp/UserConsent";

/**
 * Fetch the segments from the current profile and send them to Google Ads Manager if they changed since
 * the last time they were sent.
 * @remarks
 * This function assume that BlueConic is loaded and the profile is set up!
 * See also the Send_segments_GAM tag in GTM.
 */

/**
 * Key for the local storage slot where we are storing the segments retrieved from the CDP.
 * @remarks
 * This key is also used in the Send_segments_GAM GTM Tag to send the available segments as soon as GTM
 * is loaded.
 * @private
 */
const segmentsStorageKey = "CDP_segments";

/**
 * Key of the targeting slot in Google Publisher Tags
 */
const cdpTargetingKey = "cdp";
export default function manageSegments(consent: UserConsent): void {
  if (typeof googletag === "undefined") {
    Logger.verbose("googletag is not loaded");
    return;
  }
  const userAdapter = getUserAdapter();
  if (!userAdapter) {
    Logger.error("UserAdapter not available.");
    return;
  }

  if (userAdapter?.consent?.enabled && consent.targetedAds === false) {
    // No user consent for targeted ads.
    clearGPTTargeting(cdpTargetingKey);
    localStorage.removeItem(segmentsStorageKey);
    Logger.verbose("No consent for targeted ads.");
    return;
  }

  const segments = blueConicClient.getSegments();
  if (segments.length === 0) {
    Logger.verbose("CDP: No segments loaded");
    localStorage.removeItem(segmentsStorageKey);
    return;
  }

  const segmentIds = segments.map((segment) => segment.id);
  const userIdentifier = userAdapter.rcId ?? userAdapter.rc_visitor_id;

  if (!userIdentifier) {
    Logger.warn("Set targeting segments: Could not determine the ID of the current user. Aborting.");
    return;
  }
  sendSegmentsToGAM(userIdentifier, segmentIds, userAdapter?.consent?.enabled ?? false);

  function sendSegmentsToGAM(userIdentifier: string, segmentsIds: string[], cmpEnabled: boolean): void {
    Logger.verbose(`Sending ${segmentsIds.length} segments.`);
    setGPTTargeting(cdpTargetingKey, segmentIds);
    let storageContent: string;
    if (cmpEnabled) {
      const userTargeting: TargetingStorage = {
        userIdentifier: userIdentifier,
        identifiers: segmentsIds,
        expireOn: new Date().getTime() + 12 * 60 * 60 * 1000, // Set expiration 12 hours in the future.
      };
      storageContent = JSON.stringify(userTargeting);
    } else {
      storageContent = segmentsIds.join();
    }
    localStorage.setItem(segmentsStorageKey, storageContent);
  }
}
